import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createGTMGAEvent, isAddin } from '@jooxter/utils';
import { AuthContext } from './AuthContext';
import {
  LoginForm,
  LoginService,
  UserLoginDetailApiVersionEnum,
  AxiosInstanceEnum,
  UserLoginDetailTypeEnum,
  axiosManager,
  axiosInstanceV4,
} from '@jooxter/api';
import { signIn, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { FILTERS_STORAGE_TOKEN, useStore } from '../store';
import { AuthV3Service } from './authV3.service';
import { AuthV4Service } from './authV4.service';
import { IAuthService } from './types';
import { useFetchLoginDetails, UserQueryKeys } from '../queries';
import { CookieService } from '@jooxter/storage';
import { useHeartBeat } from '../mutations/user';
import { REDIRECT_AFTER_LOGIN } from '../routing/constants';
import { queryClient } from '../react-query.client';
import { isGuestRoute } from '../routing/helpers';
import { forgetLoggedOut, markLoggedOut } from './loggedOut';

// signOut() with an `oauth` option resolves as soon as it has assigned window.location to the
// Cognito /logout endpoint, before the browser actually leaves the page. Clearing the token at that
// point renders the login page, which is then thrown away by the full reload coming back from
// Cognito: the login page "flashes" (ND-5282). The local cleanup is deferred instead — if the
// redirect happens, the timer dies with the page; if it doesn't (no OAuth session to end), the
// cleanup still runs and the user lands on the login page.
const OAUTH_SIGNOUT_REDIRECT_GRACE_MS = 3000;

// The reload coming back from Cognito is not a clean state: the email is persisted in the `email`
// cookie and in the zustand persisted state. Once restored, the login details point to the SAML
// provider and the first 401 makes the interceptor call loginSSO(), silently signing the user back
// in through their still-open IdP session. Only the storage is purged here — the in-memory state is
// left untouched so nothing re-renders before the redirect. Another tab still open writes the email back
// at its next store update: the logged-out marker, which it cannot, keeps the interceptor from it.
const clearPersistedSession = () => {
  markLoggedOut();
  CookieService.deleteCookie('email');
  CookieService.deleteCookie(REDIRECT_AFTER_LOGIN);
  localStorage.removeItem(FILTERS_STORAGE_TOKEN);
};

const deferUntilOAuthSignOutRedirect = (cleanup: () => void) => {
  clearPersistedSession();
  window.setTimeout(cleanup, OAUTH_SIGNOUT_REDIRECT_GRACE_MS);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [email, token, setToken, deleteToken, setEmail, setAxiosInstance] = useStore((state) => [
    state.email,
    state.token,
    state.setToken,
    state.deleteToken,
    state.setEmail,
    state.setAxiosInstance,
  ]);
  const [heartBeatDone, setHeartBeatDone] = useState<boolean>(false);
  const { mutateAsync } = useHeartBeat();
  const { loginDetails: userLoginDetail } = useFetchLoginDetails(email);
  const shouldBeMigratedToCognito = userLoginDetail?.shouldBeMigratedToCognito;
  const authService: IAuthService = useMemo(
    () => (shouldBeMigratedToCognito ? AuthV4Service.getInstance() : AuthV3Service.getInstance()),
    [shouldBeMigratedToCognito]
  );
  const pathname = window.location.pathname;

  useLayoutEffect(() => {
    const emailFromCookie = CookieService.getCookie('email');
    if (emailFromCookie && !email && !isGuestRoute(pathname)) {
      setEmail(emailFromCookie);
    }

    AuthV4Service.getInstance()
      .getToken()
      .then((t) => {
        if (t) {
          setAxiosInstance(AxiosInstanceEnum.axiosInstanceV4);
          setToken(t);
        }
      })
      .catch(() => {
        // No valid V4 session at mount (e.g. an expired refresh token) — this is an expected
        // outcome, not an error to surface. Deliberately not calling deleteToken() here: this
        // check runs once, only at mount, so a rejection can only mean "no session existed when
        // the app loaded" (the `if (t)` above already guards the success path against clobbering
        // anything set later). Clearing the token here too would race with the definitive
        // check-and-clear below and risk wiping out a token set by a login that happens while
        // this promise is still in flight.
      });

    // we want this to run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token && !heartBeatDone) {
      mutateAsync().then(() => setHeartBeatDone(true));
    }
  }, [token, heartBeatDone, mutateAsync]);

  const onLoginSuccess = useCallback(
    (token: string) => {
      authService.setToken?.(token);
      createGTMGAEvent('Login', 'Check Password', 'Valid Password');
      setToken(token);
    },
    [authService, setToken]
  );

  const loginWithProvider = useCallback((name: string) => {
    forgetLoggedOut();
    return signInWithRedirect({
      provider: { custom: name },
    });
  }, []);

  // Only called once the user is not logged out (shouldSignBackInWithSSO), or by the user signing in.
  const loginSSO = useCallback((): Promise<void | Error> => {
    forgetLoggedOut();
    if (!userLoginDetail) {
      return Promise.reject(new Error('No user login detail found'));
    }

    if (userLoginDetail.shouldBeMigratedToCognito && userLoginDetail.type === UserLoginDetailTypeEnum.Saml2) {
      if (userLoginDetail.cognitoFederatedIdentityProvider) {
        return loginWithProvider(userLoginDetail.cognitoFederatedIdentityProvider);
      } else {
        // if cognitoFederatedIdentityProvider not found, attempt to login with SSO V3
        console.warn('No cognito federated identity provider found, attempting to login with SSO V3');
      }
    }

    if (!userLoginDetail.redirect) {
      return Promise.reject(new Error('No redirection url found'));
    }

    window.location.assign(userLoginDetail.redirect);
    return Promise.resolve();
  }, [userLoginDetail, loginWithProvider]);

  const login = useCallback(
    ({ email, password }: LoginForm) => {
      if (!email) {
        return Promise.reject(new Error('Email is required'));
      }

      forgetLoggedOut();

      if (authService instanceof AuthV3Service) {
        return LoginService.logIn({ email, password }).then(onLoginSuccess);
      }

      return signIn({
        username: email.toLowerCase(),
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
          preferredChallenge: 'PASSWORD_SRP', // or 'PASSWORD'
        },
      })
        .then(() => authService.getToken(true))
        .then((accessToken) => {
          if (!accessToken) {
            throw new Error('No access token found');
          }

          if (userLoginDetail?.apiVersion === UserLoginDetailApiVersionEnum.V3) {
            queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetUserLoginDetails] });
            const decoded = jwtDecode<JwtPayload & { username?: string }>(accessToken);
            const username = decoded?.username;

            if (!username) {
              throw new Error('No username found in payload');
            }

            setToken(accessToken);
            setAxiosInstance(AxiosInstanceEnum.axiosInstanceV4);
          } else {
            onLoginSuccess(accessToken);
          }
        });
    },
    [authService, onLoginSuccess, userLoginDetail?.apiVersion, setToken, setAxiosInstance]
  );

  const logout = useCallback(async () => {
    const isAddinContext = isAddin();

    const clearLocalSession = () => {
      queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetUserLoginDetails] });
      queryClient.invalidateQueries({ queryKey: [UserQueryKeys.GetMe] });
      CookieService.deleteCookie(REDIRECT_AFTER_LOGIN);
      deleteToken();
    };

    if (!userLoginDetail) {
      if (!isAddinContext) {
        // the email has been manually deleted from localstorage, this should not happend
        // try both logout methods
        await signOut({
          global: false,
          oauth: {
            redirectUrl: window.location.origin,
          },
        });
        authService?.deleteToken && authService.deleteToken();
        deferUntilOAuthSignOutRedirect(clearLocalSession);
        return;
      } else {
        localStorage.clear();
      }
    }

    if (userLoginDetail?.apiVersion === UserLoginDetailApiVersionEnum.V4) {
      if (!isAddinContext) {
        const isSaml = userLoginDetail?.type === UserLoginDetailTypeEnum.Saml2;
        await signOut(
          isSaml
            ? {
                global: false,
                oauth: {
                  redirectUrl: window.location.origin,
                },
              }
            : undefined
        );
        if (isSaml) {
          deferUntilOAuthSignOutRedirect(clearLocalSession);
          return;
        }
      } else {
        authService?.deleteToken?.();
        localStorage.clear();
      }
    } else {
      const authInstance = AuthV3Service.getInstance();
      if (authInstance.deleteToken) {
        authInstance.deleteToken(isAddinContext ? { sameSite: 'none' } : undefined);
      }

      if (isAddinContext) {
        // jimo is not included in the addin
        localStorage.clear();
      }
    }

    clearLocalSession();
  }, [authService, deleteToken, userLoginDetail]);

  useEffect(() => {
    if (authService instanceof AuthV4Service) {
      setAxiosInstance(AxiosInstanceEnum.axiosInstanceV4);
    } else if (axiosManager.getInstance() === axiosInstanceV4) {
      setAxiosInstance(AxiosInstanceEnum.axiosInstanceV3);
    }

    authService
      .getToken()
      .then((token) => {
        if (token) {
          setToken(token);
        }
      })
      .catch(() => {
        deleteToken();
      });
  }, [authService, deleteToken, setAxiosInstance, setToken]);

  return <AuthContext.Provider value={{ login, logout, loginSSO, loginWithProvider }}>{children}</AuthContext.Provider>;
};
